import { HttpBackend, HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PendingVideoUploadResponse } from '@pixacare/dtos';
import {
  FileMeta,
  FileUploader,
  getContentTypeFromFileName,
  PartBody,
  StartUploadResponse,
  TaskScheduler,
  UploadHandler,
  UploadPartResult,
} from '@pixacare/file-upload';
import { filter, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { createUploadProgressTracker } from '../upload-progress/create-upload-progress-tracker';
import { VideoUploadEvent } from './models/video-upload-event';

import { VideoUploadItem } from './models/video-upload-item';

@Injectable({
  providedIn: 'root',
})
export class VideoUploadHttpService {
  private readonly http = inject(HttpClient);
  // Interceptor-free client so the presigned PUT do not interfere with main api interceptors.
  private readonly storageHttp = new HttpClient(inject(HttpBackend));
  // Abort controllers keyed by video id.
  private readonly uploadAbortControllers = new Map<string, AbortController>();
  private readonly scheduler = new TaskScheduler(1);

  getUploadSessions(clientCode: string): Observable<PendingVideoUploadResponse[]> {
    return this.http.get<PendingVideoUploadResponse[]>(`${environment.apiBaseUrl}/VideoUpload/${clientCode}`);
  }

  abortUpload(videoId: string): void {
    this.uploadAbortControllers.get(videoId)?.abort();
    this.uploadAbortControllers.delete(videoId);
  }

  // Aborts every live upload at once (e.g. on logout).
  abortActiveUploads(): void {
    this.uploadAbortControllers.forEach((controller) => controller.abort());
    this.uploadAbortControllers.clear();
  }

  // Aborts an ongoing server upload session so it stops being listed as pending.
  abortUploadSession(clientCode: string, sessionToken: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiBaseUrl}/VideoUpload/${clientCode}/${sessionToken}`);
  }

  /**
   * Uploads a sequence's videos as one batch, emitting a `VideoUploadEvent` per video (keyed by its
   * index in `videos`) as it progresses, succeeds or fails, then completing once they all settle.
   * Progress is in bytes and advances while a part is being pushed, not only once it lands, and
   * carries the seconds left at the video's recent speed (see `createRemainingTimeEstimator`).
   * Each video carries its own per-source `abortSignal`, so `abortUpload` can cancel one on its own
   * (the lib drops just that video and discards its server session) without touching its siblings.
   * The batch's sessions are created right away — so a reload mid-upload still shows them all as
   * pending — while their parts queue behind whatever is already uploading (the shared scheduler).
   * A failing video doesn't stop the others — it is emitted as an `error` event, not an Observable
   * error — so the stream only completes, even on an abort.
   *
   * Returning an Observable (rather than a Promise + callbacks) lets a caller map the events
   * straight to its own actions without wrapping this in its own Observable.
   *
   * A video carrying a `sessionToken` resumes that session instead of starting a new one, which is
   * how an interrupted upload only sends the chunks the server is still missing.
   */
  uploadVideos(clientCode: string, sequenceId: number, videos: VideoUploadItem[]): Observable<VideoUploadEvent> {
    const baseUrl = `${environment.apiBaseUrl}/VideoUpload/${clientCode}/${sequenceId}`;

    return new Observable<VideoUploadEvent>((subscriber) => {
      const videoIds = videos.map(({ videoId }) => videoId);

      const controllers = videoIds.map((videoId) => {
        const controller = new AbortController();
        this.uploadAbortControllers.set(videoId, controller);
        return controller;
      });

      // Byte counting, throttling and time left, keyed by video id — see the tracker.
      const videoIndexById = new Map(videoIds.map((videoId, videoIndex) => [videoId, videoIndex]));
      const progressTracker = createUploadProgressTracker(
        videos.map(({ videoId, file }) => ({ id: videoId, totalBytes: file.size })),
        ({ id, syncedBytes, totalBytes, remainingSeconds }) => {
          const videoIndex = videoIndexById.get(id);
          if (videoIndex === undefined) {
            return;
          }
          subscriber.next({ type: 'progress', videoIndex, syncedBytes, totalBytes, remainingSeconds });
        },
      );

      new FileUploader(
        this.buildHandler(clientCode, baseUrl, {
          onPartProgress: progressTracker.reportPartBytes,
          onPartSettled: progressTracker.partEnded,
        }),
        this.scheduler,
      )
        .upload(
          videos.map(({ file, videoId, sessionToken }, videoIndex) => ({
            fileMeta: this.toFileMeta(file),
            readPart: (offset: number, length: number): Promise<Blob> => {
              const part = file.slice(offset, offset + length);
              progressTracker.trackPart(part, videoId);
              return Promise.resolve(part);
            },
            sessionToken,
            abortSignal: controllers[videoIndex].signal,
          })),
          {
            concurrentRequests: 2,
            // The lib counts parts, not bytes, and never exposes the part size — so scale the ratio
            // by the file size (all parts are equal but the last one).
            onFileProgress: (videoIndex, done, total) =>
              progressTracker.reportStoredBytes(
                videoIds[videoIndex],
                total === 0 ? 0 : (done / total) * videos[videoIndex].file.size,
              ),
            onFileSettled: (result) => {
              // The video reached an end (done, failed or cancelled) — its controller is spent.
              this.uploadAbortControllers.delete(videoIds[result.fileIndex]);
              if (result.status === 'fulfilled') {
                subscriber.next({ type: 'success', videoIndex: result.fileIndex });
                return;
              }
              subscriber.next({
                type: 'error',
                videoIndex: result.fileIndex,
                error: result.error instanceof Error ? result.error.message : String(result.error),
                // Carried so a retry can resume that session instead of restarting from scratch.
                sessionToken: result.sessionToken,
              });
            },
          },
        )
        .catch(() => undefined)
        .finally(() => {
          videoIds.forEach((videoId) => this.uploadAbortControllers.delete(videoId));
          subscriber.complete();
        });
    });
  }

  private toFileMeta(file: File): FileMeta {
    return {
      fileName: file.name,
      fileSize: file.size,
      lastModified: file.lastModified,
      // Containers like .mkv report an empty MIME type in the browser.
      contentType: file.type || getContentTypeFromFileName(file.name),
    };
  }

  private buildHandler(
    clientCode: string,
    baseUrl: string,
    partReporter: {
      onPartProgress: (part: PartBody, loadedBytes: number) => void;
      onPartSettled: (part: PartBody) => void;
    },
  ): UploadHandler {
    return {
      api: {
        start: (body, requestSignal) =>
          this.toPromise(this.http.post<StartUploadResponse>(`${baseUrl}/start`, body), requestSignal),
        complete: (body, requestSignal) =>
          this.toPromise(this.http.post<void>(`${baseUrl}/complete`, body), requestSignal).then(() => undefined),
        // The abort route is scoped by client, not by sequence — matches abortUploadSession above.
        abort: (body) =>
          this.toPromise(
            this.http.delete<void>(`${environment.apiBaseUrl}/VideoUpload/${clientCode}/${body.sessionToken}`),
          ).then(() => undefined),
      },
      storage: {
        // `reportProgress` turns the PUT into an event stream: the XHR's upload-progress ticks feed
        // the byte-level progress, and only the final response resolves the handler's promise.
        uploadPart: (url, body, requestSignal): Promise<UploadPartResult> =>
          this.toPromise(
            this.storageHttp
              .put(url, body, {
                headers: { 'Content-Type': 'application/octet-stream' },
                observe: 'events',
                reportProgress: true,
                responseType: 'arraybuffer',
              })
              .pipe(
                tap((event) => {
                  if (event.type === HttpEventType.UploadProgress) {
                    partReporter.onPartProgress(body, event.loaded);
                  }
                }),
                filter((event): event is HttpResponse<ArrayBuffer> => event.type === HttpEventType.Response),
              ),
            requestSignal,
          )
            .finally(() => partReporter.onPartSettled(body))
            .then((response) => ({ eTag: response.headers.get('etag') })),
      },
    };
  }

  // Bridges an Angular HttpClient request to the handler's AbortSignal: aborting
  // unsubscribes to cancel the in-flight request and rejects the returned promise.
  private toPromise<T>(source: Observable<T>, requestSignal?: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (requestSignal?.aborted) {
        reject(requestSignal.reason ?? new DOMException('Aborted', 'AbortError'));
        return;
      }
      const subscription = source.subscribe({ next: resolve, error: reject });
      requestSignal?.addEventListener(
        'abort',
        () => {
          subscription.unsubscribe();
          reject(requestSignal.reason ?? new DOMException('Aborted', 'AbortError'));
        },
        { once: true },
      );
    });
  }
}
